

import type { FilesystemPlugin, Directory } from '@capacitor/filesystem';
import type { FileTransferPlugin } from '@capacitor/file-transfer';

import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { App } from '@capacitor/app'

const is_browser = !(typeof window === 'undefined');

const filesystem = {
   Filesystem: null as unknown as FilesystemPlugin,
   Directory: null as unknown as typeof Directory,
   FileTransfer: null as unknown as FileTransferPlugin,

   async import_modules() {
      if (this.Filesystem && this.Directory && this.FileTransfer) return true

      const [{ Filesystem, Directory }, { FileTransfer }] = await Promise.all([import('@capacitor/filesystem'), import('@capacitor/file-transfer')])
      this.FileTransfer = FileTransfer
      this.Filesystem = Filesystem
      this.Directory = Directory
   },
   async blob_to_base64(blob?: Blob): Promise<string | null> {
      if (!blob) return null
      return new Promise((resolve) => {
         const reader = new FileReader();
         reader.onload = () => {
            const base64String = (reader.result as string).split(',')[1];
            resolve(base64String);
         };
         reader.onerror = () => resolve(null);
         reader.readAsDataURL(blob);
      });
   },

   async download_audio_file(
      url: string,
      target_directory: 'cache' | 'data' | 'documents' = 'cache'
   ): Promise<string | null> {
      if (!is_browser) return null
      try {
         await this.import_modules()
         if (!url) return null;
         const file_name = url.split('/').pop() as string;
         const directory =
            target_directory === 'cache'
               ? this.Directory.Cache
               : target_directory === 'documents'
                  ? this.Directory.Documents
                  : this.Directory.Data;

         if (
            target_directory === 'documents' &&
            (await this.Filesystem.checkPermissions()).publicStorage !== 'granted'
         ) {
            const { publicStorage } = await this.Filesystem.requestPermissions();
            if (publicStorage === 'denied') return null;
         }

         const file_data = await filesystem.read_file(file_name, target_directory);
         if (file_data) return file_data;

         const fileInfo = await this.Filesystem.getUri({
            directory,
            path: file_name
         });

         await this.FileTransfer.downloadFile({
            url,
            path: fileInfo.uri,
         });

         const data = await filesystem.read_file(file_name);
         return data ?? null;
      } catch (error) {
         console.error({ at: 'error at download_audio_file', error });
         return null;
      }
   },

   async save_file(
      file_name: string,
      data: string | Blob,
      target_directory: 'cache' | 'data' | 'documents' | 'externalStorage' = 'cache'
   ): Promise<string | null> {
      if (!is_browser) return null
      try {
         if (!data) return null;
         await this.import_modules()

         let directory:
            | Directory.Documents
            | Directory.Data
            | Directory.Cache
            | Directory.ExternalStorage
            | Directory.Library;
         switch (target_directory) {
            case 'cache':
               directory = this.Directory.Cache;
               break;
            case 'data':
               directory = this.Directory.Data;
               break;
            case 'documents':
               directory = this.Directory.Documents;
               break;
            case 'externalStorage':
               directory = this.Directory.ExternalStorage;
               break;
         }

         if (
            (target_directory === 'documents' || target_directory === 'externalStorage') &&
            (await cap_utils.device.get_device_os()) === 'android' &&
            (await this.Filesystem.checkPermissions()).publicStorage !== 'granted'
         ) {
            const { publicStorage } = await this.Filesystem.requestPermissions();
            if (publicStorage === 'denied') return null;
         }

         const file = await this.Filesystem.writeFile({
            path: file_name,
            data,
            directory
         });

         return file.uri ?? null;
      } catch (error) {
         console.error({ at: 'error at download_audio_file', error });
         return null;
      }
   },

   async read_file(
      filename: string,
      target_directory: 'cache' | 'data' | 'documents' | 'externalStorage' = 'cache'
   ): Promise<string | null> {
      if (!is_browser) return null
      try {
         await this.import_modules()

         let directory:
            | Directory.Documents
            | Directory.Data
            | Directory.Cache
            | Directory.ExternalStorage
            | Directory.Library;
         switch (target_directory) {
            case 'cache':
               directory = this.Directory.Cache;
               break;
            case 'data':
               directory = this.Directory.Data;
               break;
            case 'documents':
               directory = this.Directory.Documents;
               break;
            case 'externalStorage':
               directory = this.Directory.ExternalStorage;
               break;
         }

         if (
            (target_directory === 'documents' || target_directory === 'externalStorage') &&
            (await cap_utils.device.get_device_os()) === 'android' &&
            (await this.Filesystem.checkPermissions()).publicStorage !== 'granted'
         ) {
            const { publicStorage } = await this.Filesystem.requestPermissions();
            if (publicStorage === 'denied') return null;
         }

         const { data } = await this.Filesystem.readFile({
            directory,
            path: filename
         });

         if (!data) return null;
         return data as string;
      } catch (error) {
         console.error({ at: 'error at get_file_uri', error });
         return null;
      }
   }
}

const device = {
   is_device_native() {
      if (!is_browser) return false
      return Capacitor.isNativePlatform() || false
   },

   async get_device_os(): Promise<'ios' | 'android' | 'web' | 'server'> {
      if (!is_browser) return 'server';

      const info = await Device?.getInfo();
      return info?.platform || 'web';
   }
}

export const cap_utils = {
   filesystem,
   device,
}
